import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Web as WebIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmDialog } from './common';

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 64;

interface AdminLayoutProps {
  children?: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  
  // サイト編集ページかどうかを判定
  const isEditPage = location.pathname.includes('/sites/') && 
                     (location.pathname.includes('/edit') || location.pathname.includes('/create'));
  
  // サイト編集ページでは自動でメニューを閉じる
  useEffect(() => {
    if (isEditPage) {
      setCollapsed(true);
    }
  }, [location.pathname, isEditPage]);
  
  const drawerWidth = collapsed ? drawerWidthCollapsed : drawerWidthExpanded;
  
  // サイト編集ページ以外では、メニューの開閉に応じて左マージンを調整
  // メニューから24pxの余白を保つ（メインコンテンツのパディング24pxを含む）
  // サイト編集ページは現在のまま（drawerWidthCollapsed）
  const mainMarginLeft = isEditPage 
    ? drawerWidthCollapsed 
    : drawerWidth;
  
  const displayUser = React.useMemo(() => {
    return currentUser || {
      id: 'guest',
      displayName: 'ゲストユーザー',
      email: 'guest@example.com'
    };
  }, [currentUser]);

  const handleDrawerToggle = () => {
    if (isMobile) {
    setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('ログアウトに失敗しました:', error);
    } finally {
      setLogoutDialogOpen(false);
    }
  };

  const isViewer = currentUser?.role === 'viewer';
  const menuItems = isViewer ? [
    {
      text: 'サイト管理',
      icon: <WebIcon />,
      path: '/admin/sites',
    },
  ] : [
    {
      text: 'ダッシュボード',
      icon: <DashboardIcon />,
      path: '/admin/dashboard',
    },
    {
      text: 'サイト管理',
      icon: <WebIcon />,
      path: '/admin/sites',
    },
    {
      text: 'ユーザー管理',
      icon: <PeopleIcon />,
      path: '/admin/users',
    },
  ];

  const isActiveMenuItem = (path: string) => {
    if (path === '/admin/sites') {
      return location.pathname.startsWith('/admin/sites');
    }
    if (path === '/admin/users') {
      return location.pathname.startsWith('/admin/users');
    }
    return location.pathname === path;
  };

  // すべてのアイコンを同じ縦のライン上に配置するための共通スタイル
  const iconContainerStyle = {
    width: '64px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const drawer = (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* ハンバーガーアイコンとロゴ */}
      {!isMobile && (
        <Box sx={{ py: 2, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <Box sx={iconContainerStyle}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ 
                color: '#000000',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Box 
            onClick={() => {
              if (isViewer) {
                navigate('/admin/sites');
              } else {
                navigate('/admin');
              }
            }}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              transition: theme.transitions.create(['opacity', 'max-width'], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
              opacity: collapsed ? 0 : 1,
              maxWidth: collapsed ? 0 : '200px',
              overflow: 'hidden',
              ml: collapsed ? 0 : 1,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Box
              component="img"
              src="/assets/images/logo_blue.svg"
              alt="Qwipe"
              sx={{
                height: '28px',
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
            />
            <Typography 
              variant="h6" 
              component="span"
              sx={{ 
                color: '#1B76D2',
                fontWeight: 400,
                fontSize: '1.35rem',
                whiteSpace: 'nowrap',
                position: 'relative',
                top: '-2px',
              }}
            >
              Admin
            </Typography>
          </Box>
        </Box>
      )}

      {/* モバイル用ヘッダー */}
      {isMobile && (
        <Toolbar sx={{ px: 3, py: 3, minHeight: '64px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h6" 
              component="span"
              sx={{ 
                color: '#1B76D2',
                fontWeight: 600,
                fontSize: '1.25rem',
              }}
            >
              Qwipe
            </Typography>
            <Typography 
              variant="h6" 
              component="span"
              sx={{ 
                color: '#000000',
                fontWeight: 400,
                fontSize: '1.25rem',
              }}
            >
              Admin
        </Typography>
          </Box>
      </Toolbar>
      )}

      {/* メニューアイテム */}
      <List sx={{ flex: 1, px: 0, pb: '80px' }}>
        {menuItems.map((item) => {
          const isSelected = isActiveMenuItem(item.path);
          return (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
                selected={isSelected}
              sx={{
                  position: 'relative',
                  py: 1.5,
                  px: 0,
                  minHeight: '48px',
                '&.Mui-selected': {
                    backgroundColor: 'rgba(27, 118, 210, 0.1)',
                  '&:hover': {
                      backgroundColor: 'rgba(27, 118, 210, 0.15)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      backgroundColor: '#1B76D2',
                  },
                },
                '&.Mui-selected .MuiListItemIcon-root': {
                    color: '#1B76D2',
                },
                '&.Mui-selected .MuiListItemText-primary': {
                    color: '#1B76D2',
                    fontWeight: 500,
                    fontSize: '16px',
                  },
                  '&:not(.Mui-selected)': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                  '&:not(.Mui-selected) .MuiListItemIcon-root': {
                    color: 'rgba(0, 0, 0, 0.54)',
                  },
                  '&:not(.Mui-selected) .MuiListItemText-primary': {
                    color: '#000000',
                    fontSize: '16px',
                },
              }}
            >
                <ListItemIcon sx={{ 
                  ...iconContainerStyle,
                  minWidth: '64px',
                  margin: 0,
                  '& svg': { fontSize: '1.5rem' },
                }}>
                  {item.icon}
            </ListItemIcon>
                <Box sx={{
                  transition: theme.transitions.create(['opacity', 'max-width'], {
                    easing: theme.transitions.easing.easeInOut,
                    duration: theme.transitions.duration.standard,
                  }),
                  opacity: collapsed ? 0 : 1,
                  maxWidth: collapsed ? 0 : '200px',
                  overflow: 'hidden',
                  ml: collapsed ? 0 : 1,
                }}>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      marginLeft: 0,
                      whiteSpace: 'nowrap',
                    }}
                  />
                </Box>
          </ListItemButton>
        </ListItem>
          );
        })}
      </List>

      {/* ログアウトボタン（画面の一番下） */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        pb: 2,
      }}>
        {collapsed ? (
          <Box sx={iconContainerStyle}>
            <IconButton 
              onClick={() => setLogoutDialogOpen(true)}
              sx={{
                color: 'rgba(0, 0, 0, 0.54)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          </Box>
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              px: 0, 
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => setLogoutDialogOpen(true)}
          >
            <Box sx={iconContainerStyle}>
              <IconButton 
                sx={{
                  color: 'rgba(0, 0, 0, 0.54)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <LogoutIcon sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Box>
            <Typography 
              sx={{ 
                color: '#000000',
                fontSize: '14px',
                transition: theme.transitions.create(['opacity', 'max-width'], {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.standard,
                }),
                opacity: collapsed ? 0 : 1,
                maxWidth: collapsed ? 0 : '200px',
                overflow: 'hidden',
                ml: collapsed ? 0 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {displayUser.email || displayUser.displayName || ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box>
      <CssBaseline />
      
      {/* モバイル用メニューボタン */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                color: '#000000',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              onClick={() => {
                if (isViewer) {
                  navigate('/admin/sites');
                } else {
                  navigate('/admin');
                }
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            >
              <Box
                component="img"
                src="/assets/images/logo_blue.svg"
                alt="Qwipe"
                sx={{ height: '28px', width: 'auto' }}
              />
              <Typography 
                variant="h6" 
                component="span"
                sx={{ 
                  color: '#1B76D2',
                  fontWeight: 400,
                  fontSize: '1.35rem',
                }}
              >
                Admin
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        component="nav"
        sx={{ 
          width: { md: drawerWidth }, 
          flexShrink: { md: 0 },
          position: { md: 'fixed' },
          left: 0,
          top: 0,
          height: '100vh',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthExpanded,
              backgroundColor: '#FFFFFF',
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#FFFFFF',
              borderRight: 'none',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isEditPage ? 0 : 3,
          ml: { 
            md: `${mainMarginLeft}px`,
            transition: theme.transitions.create('margin-left', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          },
          backgroundColor: '#F5F5F5',
          minHeight: '100vh',
          pt: isEditPage ? 0 : { xs: '64px', md: 3 },
        }}
      >
        {children || <Outlet />}
      </Box>
      <ConfirmDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
        message="ログアウトしますか？"
        confirmText="ログアウト"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />
    </Box>
  );
};
